/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output
} from '@angular/core';
import {
  FormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SelectButtonModule } from 'primeng/selectbutton';

export interface SelectButtonOption {
  icon?: string;
  label?: string;
  value: unknown;
  title?: string;
}

@Component({
  template: `
    <p-selectButton
      [options]="options"
      [(ngModel)]="value"
      (onChange)="onChange($event)"
      [multiple]="multiple"
      optionLabel="label"
      optionValue="value"
    >
      <ng-template #item let-item>
        <span
          [title]="item.title | translate"
          [attr.data-cy]="'select-option-' + item.value"
          [attr.data-testid]="'select-option-' + item.value"
        >
          <i *ngIf="isIcon(item)" [class]="item.icon"></i>
          <span *ngIf="!isIcon(item)">{{ item.label | translate }}</span>
        </span>
      </ng-template>
    </p-selectButton>
  `,
  selector: 'man-select-button',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectButtonModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectButtonComponent,
      multi: true
    }
  ]
})
export class SelectButtonComponent
  implements ControlValueAccessor, AfterViewInit
{
  @Input() public options: SelectButtonOption[];
  @Input() public value: unknown;
  @Input() public multiple = false;
  @Output() public changed: EventEmitter<unknown> = new EventEmitter<any>();

  public disabled: boolean;

  private buttonRoleElements: HTMLElement[];

  constructor(private el: ElementRef) {}

  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (event.key.startsWith('Arrow')) {
      this.onArrowNavigation(event);
    }
  }

  public isIcon(item: { icon?: string }): boolean {
    return item?.icon !== undefined;
  }

  public ngAfterViewInit(): void {
    this.buttonRoleElements =
      this.el.nativeElement.querySelectorAll('[role="button"]');
  }

  public writeValue(value: unknown): void {
    this.value = value;
  }

  public registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onChange(event: any): void {
    this.value = event.value;
    this.changed.emit(this.value);
    this._onChange(this.value);
    this._onTouched();
  }

  private _onChange: (_: any) => void = () => void 0;
  private _onTouched: () => void = () => void 0;

  private onArrowNavigation(event: KeyboardEvent): void {
    const currentElement = event.target as HTMLElement;
    const currentIndex = Array.from(this.buttonRoleElements).indexOf(
      currentElement
    );

    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % this.buttonRoleElements.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex =
        (currentIndex - 1 + this.buttonRoleElements.length) %
        this.buttonRoleElements.length;
    }
    const nextElement = this.buttonRoleElements[nextIndex] as HTMLElement;
    nextElement.focus();
  }
}
